import React, { useState, useEffect } from "react";
import { Row, Col, Button, Card, Image } from "react-bootstrap";
import { FaShareAlt, FaPhoneAlt, FaEye, FaGlobe, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaThumbsUp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import BaseUrl from "../../ApiServices/BaseUrl";
import defaultProfileImage from "../../CandidateCard/Images/agencyprofile.png";
import "./AgencyCard.css";

function AgencyCard({ agency }) {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userPlan = localStorage.getItem('userPlan')?.toLowerCase() || "";
    const isEmployerOrAgencySubscribed = userPlan.includes('silver') || userPlan.includes('gold');
    const isJobSeeker = user.role === 'job seeker';

    // Candidate pay-per-use contact unlock state
    const [candidateUnlocked, setCandidateUnlocked] = useState(false);
    const [unlockingAgencyContact, setUnlockingAgencyContact] = useState(false);

    // Determine if contact should be shown
    const isSubscribed = isJobSeeker ? candidateUnlocked : isEmployerOrAgencySubscribed;

    // Check if candidate has already unlocked this agency contact
    useEffect(() => {
        if (!isJobSeeker || !user.id || !agency?.id) return;
        const checkUnlock = async () => {
            try {
                const res = await fetch(`${BaseUrl}/api/candidate/contact-unlock/check?candidate_id=${user.id}&target_user_id=${agency.id}&target_type=agency`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.unlocked) setCandidateUnlocked(true);
                }
            } catch (err) {
                console.error('Error checking agency contact unlock:', err);
            }
        };
        checkUnlock();
    }, [user.id, agency?.id, isJobSeeker]);

    const handleCandidateUnlock = async () => {
        if (!user.id) {
            Swal.fire({ icon: 'info', title: 'Login Required', text: 'Please login to unlock contact details.' });
            return;
        }
        setUnlockingAgencyContact(true);
        try {
            const res = await fetch(`${BaseUrl}/api/candidate/contact-unlock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    candidate_id: user.id,
                    target_user_id: agency.id,
                    target_type: 'agency'
                })
            });
            const data = await res.json();
            if (res.ok && (data.unlocked || data.already_unlocked)) {
                setCandidateUnlocked(true);
                Swal.fire({ icon: 'success', title: 'Contact Unlocked!', text: `Charge: ${data.charge_applied || 0} AED`, timer: 2000, showConfirmButton: false });
            } else if (res.status === 402) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Insufficient Wallet Balance',
                    html: `<p>Your wallet balance is <strong>${data.balance} AED</strong>.</p><p>Required: <strong>${data.required} AED</strong></p><p>Please top up your wallet to unlock contact details.</p>`,
                    confirmButtonText: 'OK'
                });
            } else {
                Swal.fire({ icon: 'error', title: 'Unable to Unlock', text: data.error || 'Please try again.' });
            }
        } catch (err) {
            console.error('Error unlocking agency contact:', err);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to unlock contact details.' });
        } finally {
            setUnlockingAgencyContact(false);
        }
    };

    if (!agency) return null;

    // Construct image URL
    const logoUrl = agency.company_logo
        ? `${BaseUrl}/${agency.company_logo.replace(/\\/g, '/')}`
        : defaultProfileImage;

    const handleContactAction = async (actionType) => {
        if (!user.id) {
            Swal.fire({
                title: '<div class="swal-custom-title">Please Register or Login</div>',
                text: `You must be registered and logged in to contact this agency via ${actionType}`,
                icon: 'info',
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Register',
                cancelButtonText: 'Login',
                denyButtonText: '<i class="bi bi-whatsapp"></i> Continue with WhatsApp',
                customClass: {
                    popup: 'registration-popup',
                    confirmButton: 'swal-register-btn',
                    cancelButton: 'swal-login-btn',
                    denyButton: 'swal-whatsapp-btn',
                    actions: 'swal-actions-column'
                },
                buttonsStyling: false,
                showCloseButton: true,
                footer: '<div class="swal-footer-text">Registration helps prevent fake profiles</div>'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/register', {
                        state: { defaultTab: 'employer' }
                    });
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    navigate('/login');
                } else if (result.isDenied) {
                    const message = encodeURIComponent('Hello, I want to register on GulfWorker.\nPlease guide me with the registration process');
                    window.open(`https://wa.me/97477651422?text=${message}`, '_blank');
                }
            });
        } else if (actionType === 'like') {
            // Add actual like functionality if needed
            Swal.fire({
                icon: 'success',
                title: 'Interested!',
                text: 'You have shown interest in this agency.',
                timer: 2000,
                showConfirmButton: false
            });
        } else {
            // Unlocking flow for Job Seekers
            if (isJobSeeker && !candidateUnlocked) {
                const result = await Swal.fire({
                    title: 'Unlock Contact',
                    html: `You need to unlock to contact this agency. Your wallet will be charged based on your Pay Per Use plan.`,
                    icon: 'info',
                    showCancelButton: true,
                    confirmButtonText: 'Unlock & Continue',
                    cancelButtonText: 'Cancel'
                });

                if (result.isConfirmed) {
                    setUnlockingAgencyContact(true);
                    try {
                        const res = await fetch(`${BaseUrl}/api/candidate/contact-unlock`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                candidate_id: user.id,
                                target_user_id: agency.id,
                                target_type: 'agency'
                            })
                        });
                        const data = await res.json();
                        setUnlockingAgencyContact(false);

                        if (res.ok && (data.unlocked || data.already_unlocked)) {
                            setCandidateUnlocked(true);
                            Swal.fire({
                                icon: 'success',
                                title: 'Contact Unlocked!',
                                text: `Charge: ${data.charge_applied || 0} AED. You can now send an enquiry.`,
                                timer: 2000,
                                showConfirmButton: false
                            });
                            // let execution continue to show form
                        } else if (res.status === 402) {
                            Swal.fire({
                                icon: 'warning',
                                title: 'Insufficient Wallet Balance',
                                html: `<p>Your wallet balance is <strong>${data.balance} AED</strong>.</p><p>Required: <strong>${data.required} AED</strong></p><p>Please top up your wallet to unlock contact details.</p>`,
                                confirmButtonText: 'Top Up Wallet',
                                showCancelButton: true,
                            }).then((topupResult) => {
                                if (topupResult.isConfirmed) {
                                    navigate('/candidate-subscription');
                                }
                            });
                            return; // Stop here
                        } else {
                            Swal.fire({ icon: 'error', title: 'Unable to Unlock', text: data.error || 'Please try again.' });
                            return; // Stop here
                        }
                    } catch (err) {
                        console.error('Error unlocking agency contact:', err);
                        setUnlockingAgencyContact(false);
                        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to unlock contact details.' });
                        return; // Stop here
                    }
                } else {
                    return; // Cancelled
                }
            }

            // Show Enquiry Form for Call and WhatsApp
            let mobileCountryCode = "";
            let mobileNumber = user.mobile_number || "";
            let whatsappCountryCode = "";
            let whatsappNumber = "";

            // Fetch latest user details to get separate mobile and WhatsApp numbers
            try {
                // Primary source: Base User API (always exists for registered users)
                const userResponse = await fetch(`${BaseUrl}/${user.id}`);
                if (userResponse.ok) {
                    const userData = await userResponse.json();

                    // Mobile number + country code
                    const rawMobileCode = userData.mobile_number_country_code || "";
                    const mobileCodeMatch = rawMobileCode.match(/^\+?(\d+)/);
                    if (mobileCodeMatch) {
                        mobileCountryCode = `+${mobileCodeMatch[1]}`;
                    }
                    mobileNumber = userData.mobile_number || mobileNumber;

                    // WhatsApp number + country code (separate fields in users table)
                    const rawWpCode = userData.whatsapp_country_code || "";
                    const wpCodeMatch = rawWpCode.match(/^\+?(\d+)/);
                    if (wpCodeMatch) {
                        whatsappCountryCode = `+${wpCodeMatch[1]}`;
                    }
                    whatsappNumber = userData.whatsapp_number || "";
                }

                // Fallback/Supplement: Role-specific APIs
                if (user.role === 'job seeker') {
                    const response = await fetch(`${BaseUrl}/api/job-seeker/${user.id}`);
                    if (response.ok) {
                        const data = await response.json();
                        // Use job-seeker WhatsApp if available
                        if (data.whatsapp_number) {
                            whatsappNumber = data.whatsapp_number;
                            const codeMatch = (data.whatsapp_country_code || "").match(/^\+?(\d+)/);
                            if (codeMatch) whatsappCountryCode = `+${codeMatch[1]}`;
                        }
                        if (!mobileCountryCode && data.whatsapp_country_code) {
                            const codeMatch = data.whatsapp_country_code.match(/^\+?(\d+)/);
                            if (codeMatch) mobileCountryCode = `+${codeMatch[1]}`;
                        }
                    }
                } else if (user.role === 'employer') {
                    const response = await fetch(`${BaseUrl}/api/employer/user/${user.id}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.whatsapp_number) {
                            whatsappNumber = data.whatsapp_number;
                            const codeMatch = (data.whatsapp_number_country_code || "").match(/^\+?(\d+)/);
                            if (codeMatch) whatsappCountryCode = `+${codeMatch[1]}`;
                        }
                        if (!mobileNumber && data.phone_number) {
                            mobileNumber = data.phone_number;
                            const codeMatch = (data.phone_country_code || "").match(/^\+?(\d+)/);
                            if (codeMatch) mobileCountryCode = `+${codeMatch[1]}`;
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching user contact details:", error);
            }

            // Fallback: if WhatsApp is still empty, use mobile number
            if (!whatsappNumber) {
                whatsappNumber = mobileNumber;
                whatsappCountryCode = whatsappCountryCode || mobileCountryCode;
            }

            const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();

            const agencyName = agency.agency_name || 'your agency';
            const messageTemplates = [
                `Greetings ${agencyName},\nWe are interested in a long-term business association for manpower recruitment. We require skilled and unskilled workers and would like to explore cooperation for sourcing suitable candidates.\n\nKindly share your company profile, terms, and process details to proceed.`,
                `Dear ${agencyName},\nWe are an employer seeking reliable manpower for our upcoming projects. We need skilled workers across departments and believe your agency can assist us efficiently.\n\nPlease share your recruitment process and fees at the earliest convenience.`,
                `Hello ${agencyName},\nI am a sub-agent interested in partnering for candidate referrals and recruitment. I have an active network of job seekers looking for Gulf and overseas opportunities.\n\nKindly share your commission terms so we can initiate a profitable cooperation.`,
                `Respected ${agencyName},\nI am a job seeker looking for employment opportunities abroad. I would like to know about available positions, visa support, and placement fees.\n\nPlease guide me through the steps and current openings.`,
                `Hi ${agencyName},\nWe are a corporate company with immediate manpower requirements for skilled trades. We seek a trusted recruitment partner for vetted candidates with timely deployment.\n\nKindly share your credentials and service charges so we can evaluate a long-term agreement.`
            ];
            let currentTemplateIndex = -1;

            Swal.fire({
                title: 'Agency Enquiry Form',
                html: `
                    <div class="text-start">
                        <div class="mb-3">
                            <input type="text" id="enquiry-name" class="form-control" placeholder="Your Name" value="${fullName}" readonly>
                        </div>
                        <div class="mb-3">
                            <input type="email" id="enquiry-email" class="form-control" placeholder="Your Email" value="${user.email || ""}" readonly>
                        </div>
                        <div class="mb-3">
                            <input type="text" id="enquiry-phone" class="form-control" placeholder="Phone Number" value="${mobileCountryCode}${mobileNumber}" readonly>
                        </div>
                        <div class="mb-3">
                            <input type="text" id="enquiry-whatsapp" class="form-control" placeholder="WhatsApp Number" value="${whatsappCountryCode}${whatsappNumber}" readonly>
                        </div>
                        <div class="mb-3">
                            <select id="enquiry-user-type" class="form-select">
                                <option value="">Select User Type</option>
                                <option value="Manpower Agency">Manpower Agency</option>
                                <option value="Sub Agent / Recruitment Partner">Sub Agent / Recruitment Partner</option>
                                <option value="Direct Employer (Company)">Direct Employer (Company)</option>
                                <option value="Family Employer (Domestic Hiring)">Family Employer (Domestic Hiring)</option>
                                <option value="Job Seeker" ${user.role === 'job seeker' ? 'selected' : ''}>Job Seeker</option>
                                <option value="Freelance Recruiter">Freelance Recruiter</option>
                                <option value="HR Manager">HR Manager</option>
                                <option value="Corporate Company">Corporate Company</option>
                                <option value="Overseas Consultant">Overseas Consultant</option>
                                <option value="Training Center">Training Center</option>
                                <option value="Visa / PRO Service Provider">Visa / PRO Service Provider</option>
                                <option value="Medical / GAMCA Center">Medical / GAMCA Center</option>
                                <option value="Trade Test Center">Trade Test Center</option>
                                <option value="Advertising / Marketing Partner">Advertising / Marketing Partner</option>
                                <option value="Software / IT Partner">Software / IT Partner</option>
                                <option value="Investor">Investor</option>
                                <option value="Business Partnership Inquiry">Business Partnership Inquiry</option>
                                <option value="Complaint / Report Issue">Complaint / Report Issue</option>
                                <option value="General Inquiry">General Inquiry</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div class="mb-1 d-flex align-items-center justify-content-between">
                            <label class="text-muted small">Message <span class="text-danger">*</span></label>
                            <button type="button" id="template-cycle-btn" style="font-size:12px;padding:3px 10px;border-radius:20px;border:1.5px solid #28a745;background:transparent;color:#28a745;cursor:pointer;font-weight:600;transition:all 0.2s;" onmouseover="this.style.background='#28a745';this.style.color='#fff'" onmouseout="this.style.background='transparent';this.style.color='#28a745'">✨ Try a Template</button>
                        </div>
                        <div class="mb-3">
                            <textarea id="enquiry-message" class="form-control" rows="5" placeholder="Enter your message here (20-50 words) or use a template above..."></textarea>
                            <div id="word-count-info" class="text-muted small mt-1">Words: 0 &nbsp;|&nbsp; <span id="template-indicator" style="color:#28a745;"></span></div>
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: 'Submit Enquiry',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#28a745',
                didOpen: () => {
                    const textarea = document.getElementById('enquiry-message');
                    const wordCountInfo = document.getElementById('word-count-info');
                    const templateBtn = document.getElementById('template-cycle-btn');
                    const templateIndicator = document.getElementById('template-indicator');

                    const updateWordCount = () => {
                        const words = textarea.value.trim().split(/\s+/).filter(word => word.length > 0).length;
                        const currentIndicator = document.getElementById('template-indicator');
                        const indicatorText = currentIndicator ? currentIndicator.innerHTML : '';
                        wordCountInfo.innerHTML = `Words: <strong>${words}</strong> &nbsp;|&nbsp; <span id="template-indicator" style="color:#28a745;">${indicatorText}</span>`;
                        if (words < 20 || words > 50) {
                            wordCountInfo.style.color = '#dc3545';
                        } else {
                            wordCountInfo.style.color = '#6c757d';
                        }
                    };

                    textarea.addEventListener('input', updateWordCount);

                    templateBtn.addEventListener('click', () => {
                        currentTemplateIndex = (currentTemplateIndex + 1) % messageTemplates.length;
                        textarea.value = messageTemplates[currentTemplateIndex];
                        wordCountInfo.innerHTML = `Words: <strong>${textarea.value.trim().split(/\s+/).filter(w => w.length > 0).length}</strong> &nbsp;|&nbsp; <span id="template-indicator" style="color:#28a745;">Template ${currentTemplateIndex + 1} of ${messageTemplates.length}</span>`;
                        wordCountInfo.style.color = '#6c757d';
                        textarea.focus();
                    });
                },
                preConfirm: () => {
                    const name = document.getElementById('enquiry-name').value;
                    const email = document.getElementById('enquiry-email').value;
                    const user_type = document.getElementById('enquiry-user-type').value;
                    const phone_number = document.getElementById('enquiry-phone').value;
                    const message = document.getElementById('enquiry-message').value;
                    const words = message.trim().split(/\s+/).filter(word => word.length > 0).length;

                    if (!message) {
                        Swal.showValidationMessage('Message is required');
                        return false;
                    }
                    if (words < 20 || words > 50) {
                        Swal.showValidationMessage('Message must be between 20 and 50 words');
                        return false;
                    }

                    return { user_id: user.id, agency_id: agency.id, name, email, user_type, phone_number, message };
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        const response = await fetch(`${BaseUrl}/api/enquiry`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(result.value)
                        });

                        if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.error || 'Failed to submit enquiry');
                        }

                        Swal.fire({
                            icon: 'success',
                            title: 'Success!',
                            text: 'Your enquiry has been submitted successfully.',
                            timer: 3000,
                            showConfirmButton: true,
                            confirmButtonColor: '#28a745'
                        });
                    } catch (error) {
                        console.error("Error submitting enquiry:", error);
                        Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
                    }
                }
            });
        }
    };

    return (
        <Card className="agency-card-reference shadow-sm">
            {/* Featured Ribbon - Only shown for Gold plan agencies */}
            {agency.isFeatured && (
            <div className="featured-ribbon-ref">
                <span>★ Featured</span>
            </div>
            )}

            <Card.Body className="pt-3 px-3 pb-2 d-flex flex-column h-100">
                {/* Agency Name Header */}
                <h6 className="agency-title-ref text-center mb-3 mt-0 fw-bold">{agency.agency_name}</h6>

                <Row className="align-items-center">
                    {/* Left Column: Logo & Verified Badge */}
                    <Col xs={4} className="text-center px-1">
                        <div className="verified-badge-ref">
                            Verified
                        </div>
                        <div className="logo-container-ref mb-1">
                            <Image src={logoUrl} roundedCircle className="agency-logo-ref" />
                        </div>
                        <div className="agency-custom-label-ref">
                            {agency.custom_label || '\u00A0'}
                        </div>
                    </Col>

                    {/* Right Column: Info Details */}
                    <Col xs={8} className="ps-2 pe-1">
                        <div className="agency-info-row">
                            <FaMapMarkerAlt className="ref-icon location-icon" />
                            <span className="info-text">{agency.city}, {agency.country}</span>
                        </div>
                        <div className="agency-info-row">
                            <FaPhoneAlt className="ref-icon phone-icon" />
                            <span className="info-text">
                                {isSubscribed
                                    ? (agency.official_phone || agency.owner_mobile || "Not specified")
                                    : "phone number"}
                            </span>
                        </div>
                        <div className="agency-info-row">
                            <FaWhatsapp className="ref-icon whatsapp-icon" />
                            <span className="info-text">
                                {isSubscribed
                                    ? (agency.whatsapp_number_1 || "Not specified")
                                    : "whatsapp number"}
                            </span>
                        </div>
                        <div className="agency-info-row">
                            <FaEnvelope className="ref-icon email-icon" />
                            <span className="info-text text-truncate">{agency.email}</span>
                        </div>
                        <div className="agency-info-row">
                            <FaGlobe className="ref-icon website-icon" />
                            {agency.website_url ? (
                                <a href={agency.website_url} target="_blank" rel="noopener noreferrer" className="info-text text-decoration-none">
                                    {agency.website_url.replace(/^https?:\/\//, '').split('/')[0]}
                                </a>
                            ) : (
                                <span className="info-text" style={{ visibility: 'hidden' }}>placeholder</span>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Bottom Row: Action Buttons */}
                <div className="action-buttons-ref mt-auto pt-2">
                    <Button variant="outline-secondary" className="ref-btn-outline" onClick={() => handleContactAction('like')}>
                        <FaThumbsUp className="me-1 ref-btn-icon" /> <span className="btn-text-ref">Like</span>
                    </Button>
                    <Button variant="outline-secondary" className="ref-btn-outline" onClick={() => handleContactAction('call')}>
                        <FaPhoneAlt className="me-1 ref-btn-icon" /> <span className="btn-text-ref">Call</span>
                    </Button>
                    <Button variant="primary" className="ref-btn-primary" href={`/agencyprofile/${agency.id}`}>
                        <FaEye className="me-1 ref-btn-icon" /> <span className="btn-text-ref">View</span>
                    </Button>
                    <Button variant="success" className="ref-btn-success" onClick={() => handleContactAction('whatsapp')}>
                        <FaWhatsapp className="me-1 ref-btn-icon" /> <span className="btn-text-ref">WhatsApp</span>
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
}

export default AgencyCard;

